import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'
import { apiFetch } from '../services/api'

// =============================================================================
// Types
// =============================================================================

export interface SectionPermission {
  section: string
  can_read: boolean
  can_create: boolean
  can_update: boolean
  can_delete: boolean
}

export interface MatchedRole {
  role_id: string
  role_name: string
  match_type: 'email' | 'username' | 'idp_group'
  match_value: string
}

export interface UserPermissions {
  user_email: string | null
  user_name: string | null
  role_id: string | null
  role_name: string | null
  matched_roles: MatchedRole[]
  is_admin: boolean
  can_view_pii: boolean
  permissions: Record<string, SectionPermission>
}

interface PermissionsContextValue {
  permissions: UserPermissions | null
  isLoading: boolean
  error: string | null
  accessDenied: boolean
  refresh: () => Promise<void>
  can: (section: string, action: 'read' | 'create' | 'update' | 'delete') => boolean
  canRead: (section: string) => boolean
  canCreate: (section: string) => boolean
  canUpdate: (section: string) => boolean
  canDelete: (section: string) => boolean
  canViewPii: () => boolean
}

const defaultPermissions: UserPermissions = {
  user_email: null,
  user_name: null,
  role_id: null,
  role_name: null,
  matched_roles: [],
  is_admin: false,
  can_view_pii: false,
  permissions: {},
}

const PermissionsContext = createContext<PermissionsContextValue>({
  permissions: null,
  isLoading: true,
  error: null,
  accessDenied: false,
  refresh: async () => {},
  can: () => false,
  canRead: () => false,
  canCreate: () => false,
  canUpdate: () => false,
  canDelete: () => false,
  canViewPii: () => false,
})

// =============================================================================
// Provider
// =============================================================================

interface PermissionsProviderProps {
  children: ReactNode
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const { isAuthenticated, isLoading: authLoading, user, isEnabled } = useAuth()
  const [permissions, setPermissions] = useState<UserPermissions | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accessDenied, setAccessDenied] = useState(false)

  const fetchPermissions = async () => {
    // If auth is disabled, grant full admin access (dev mode)
    if (!isEnabled) {
      setPermissions({
        user_email: null,
        user_name: null,
        role_id: null,
        role_name: 'admin (dev mode)',
        matched_roles: [],
        is_admin: true,
        can_view_pii: true,
        permissions: {},
      })
      setIsLoading(false)
      return
    }

    // If not authenticated, no permissions
    if (!isAuthenticated || !user) {
      setPermissions(defaultPermissions)
      setIsLoading(false)
      return
    }

    try {
      // Use apiFetch which automatically includes all required auth headers
      // (X-User-ID, X-Org-ID, X-User-Email, X-IDP-Groups)
      const response = await apiFetch('/api/v1/access/me')

      if (response.status === 403) {
        // User is authenticated but not authorized (no role assignment)
        setAccessDenied(true)
        setPermissions(defaultPermissions)
        setError(null)  // Clear any previous errors
        setIsLoading(false)
        return
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch permissions: ${response.status}`)
      }

      const data = await response.json()
      setPermissions(data)
      setError(null)
      setAccessDenied(false)  // Reset access denied flag on success
    } catch (err) {
      console.error('Error fetching permissions:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch permissions')
      // On network/server error, deny access (fail closed) and show access denied page
      setPermissions(defaultPermissions)
      setAccessDenied(true)
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch permissions when auth state changes
  useEffect(() => {
    if (!authLoading) {
      fetchPermissions()
    }
  }, [authLoading, isAuthenticated, user?.email])

  // Permission check helpers
  const checkPermission = (section: string, action: 'read' | 'create' | 'update' | 'delete'): boolean => {
    // Admin always has access
    if (permissions?.is_admin) {
      return true
    }

    // No permissions loaded yet
    if (!permissions?.permissions) {
      return false
    }

    const perms = permissions.permissions

    // Direct match
    if (perms[section]) {
      return perms[section][`can_${action}`] ?? false
    }

    // Check parent section (e.g., 'integrations' for 'integrations.webhooks')
    if (section.includes('.')) {
      const parent = section.split('.')[0]
      if (perms[parent]) {
        return perms[parent][`can_${action}`] ?? false
      }
    }

    return false
  }

  const canViewPii = (): boolean => {
    // Admins can always view PII
    if (permissions?.is_admin) {
      return true
    }
    // Check explicit can_view_pii flag
    return permissions?.can_view_pii ?? false
  }

  const value: PermissionsContextValue = {
    permissions,
    isLoading: authLoading || isLoading,
    error,
    accessDenied,
    refresh: fetchPermissions,
    can: checkPermission,
    canRead: (section) => checkPermission(section, 'read'),
    canCreate: (section) => checkPermission(section, 'create'),
    canUpdate: (section) => checkPermission(section, 'update'),
    canDelete: (section) => checkPermission(section, 'delete'),
    canViewPii,
  }

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  )
}

// =============================================================================
// Hook
// =============================================================================

export function usePermissions() {
  const context = useContext(PermissionsContext)
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider')
  }
  return context
}

// =============================================================================
// Conditional Render Component
// =============================================================================

interface CanProps {
  section: string
  action?: 'read' | 'create' | 'update' | 'delete'
  children: ReactNode
  fallback?: ReactNode
}

export function Can({ section, action = 'read', children, fallback = null }: CanProps) {
  const { can, isLoading } = usePermissions()

  // While loading, show nothing (or could show skeleton)
  if (isLoading) {
    return null
  }

  if (can(section, action)) {
    return <>{children}</>
  }

  return <>{fallback}</>
}
